/**
 * Valuation Lines Calculator
 *
 * Calculates statistical bands based on visible data range.
 * Shows average and standard deviation bands as horizontal lines.
 *
 * Algorithm:
 * 1. Extract field values from ALL visible bars (e.g., Close prices)
 * 2. Calculate average using selected method (mean/median/harmonic)
 * 3. Calculate standard deviation
 * 4. Return horizontal lines: average, ±1σ, ±2σ, ±3σ
 *
 * Key characteristics:
 * - DYNAMIC: Recalculates based on visible chart range (zoom/pan)
 * - Same values for all bars in visible range
 * - 7 horizontal lines: average, +1σ, -1σ, +2σ, -2σ, +3σ, -3σ
 * - Three average methods: mean (arithmetic), median, harmonic
 *
 * Reference: ChartIQ "val lines" (studiesChartIQ.js lines 959-980)
 */

import { isNotDefined, getSourcePathAccessor } from "../utils";

const defaultOptions = {
  sourcePath: "close", // Field to analyze
  averageType: "mean", // "mean", "median", or "harmonic"
};

export default function valuationLines() {
  let options = defaultOptions;

  /**
   * Calculate arithmetic mean (average)
   */
  function calculateMean(values) {
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  }

  /**
   * Calculate median (middle value)
   */
  function calculateMedian(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      // Even number of values: average of two middle values
      return (sorted[mid - 1] + sorted[mid]) / 2;
    } else {
      // Odd number of values: middle value
      return sorted[mid];
    }
  }

  /**
   * Calculate harmonic mean (1 / average of reciprocals)
   * Used for rates and ratios
   */
  function calculateHarmonic(values) {
    // Filter out zero values to avoid division by zero
    const nonZero = values.filter((v) => v !== 0);

    if (nonZero.length === 0) {
      return 0;
    }

    const sumOfReciprocals = nonZero.reduce((acc, val) => acc + 1 / val, 0);
    return nonZero.length / sumOfReciprocals;
  }

  /**
   * Calculate standard deviation
   */
  function calculateStdDev(values, mean) {
    const squaredDiffs = values.map((v) => Math.pow(v - mean, 2));
    const avgSquaredDiff =
      squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
    return Math.sqrt(avgSquaredDiff);
  }

  function calculator(data) {
    const { sourcePath, averageType } = options;

    // Safety check for null/undefined data
    if (!data || data.length === 0) {
      return [];
    }

    const source = getSourcePathAccessor(sourcePath);

    // Step 1: Extract all valid values from visible range
    const values = [];
    for (let i = 0; i < data.length; i++) {
      const value = source(data[i], i);
      if (!isNotDefined(value) && isFinite(value)) {
        values.push(value);
      }
    }

    // Need at least 2 values for meaningful statistics
    if (values.length < 2) {
      return data.map(() => undefined);
    }

    // Step 2: Calculate average using selected method
    let average;
    if (averageType === "median") {
      average = calculateMedian(values);
    } else if (averageType === "harmonic") {
      average = calculateHarmonic(values);
    } else {
      // Default to mean
      average = calculateMean(values);
    }

    // Step 3: Calculate standard deviation
    const stdDev = calculateStdDev(values, average);

    // Step 4: Calculate band levels
    const levels = {
      average: average,
      plusOneStdDev: average + stdDev,
      minusOneStdDev: average - stdDev,
      plusTwoStdDev: average + 2 * stdDev,
      minusTwoStdDev: average - 2 * stdDev,
      plusThreeStdDev: average + 3 * stdDev,
      minusThreeStdDev: average - 3 * stdDev,
    };

    // Step 5: Return same levels for every bar (horizontal lines)
    return data.map(() => ({ ...levels }));
  }

  /**
   * Returns the number of undefined values at the start
   */
  calculator.undefinedLength = function () {
    // No undefined values - uses all visible data
    return 0;
  };

  /**
   * Gets or sets calculator options
   */
  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
